import React, { useContext, useEffect, useRef, useState } from 'react';
import { PageContext } from '../frontend/pageContext';

const BannerHistory = () => {
  const { versionsHistory, pathname, docSpace } = useContext<any>(PageContext);
  const [showMajor, setShowMajor] = useState(true);
  const [list, setList] = useState<string[]>([]);
  const [style, setStyle] = useState({});
  const [show, setShow] = useState(false);
  const [groupedVersions, setGroupedVersions] = useState<
    Record<string, { versions: string[]; isOpen: boolean }>
  >({});
  const inputRef = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !inputRef.current.contains(event.target) &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update the list of versions to display based on whether major versions are shown and update the width for dropdown
  useEffect(() => {
    setList(
      (showMajor ?
        versionsHistory.majorVersions :
        versionsHistory.versions
      ).filter((ver) => ver !== docSpace?.semver),
    );
    if (show) {
      adjustDropdownWidth();
    }
  }, [showMajor, show, versionsHistory, docSpace, groupedVersions]);

  // Create a grouped version object for dropdown subVersions
  // Example format: { 'v1': { versions: ['1.0.0', '1.0.1'], isOpen: false } }
  useEffect(() => {
    // Group versions by their major version number. For each version, extract the major version number,
    // Create a key (e.g., "v1"), and initialize an object with an empty versions array and isOpen false if it doesn't already exist.
    // Then, push the version into the corresponding versions array.
    const grouped = versionsHistory.versions.reduce(
      (acc, version) => {
        const majorVersion = `v${version.split('.')[0]}`;
        if (!acc[majorVersion]) {
          acc[majorVersion] = { versions: [], isOpen: false };
        }
        acc[majorVersion].versions.push(version);
        return acc;
      },
      {} as Record<string, { versions: string[]; isOpen: boolean }>,
    );
    setGroupedVersions(grouped);
  }, []);

  // Adjust the dropdown width to match the input width
  const adjustDropdownWidth = () => {
    // Use setTimeout to keep up with DOM since it is updated asynchronously
    setTimeout(() => {
      // Reset the width to auto to get the correct new width
      setStyle({});
      const inputWidth = parseFloat(
        window.getComputedStyle(inputRef.current).width,
      );
      const dropdownWidth = parseFloat(
        window.getComputedStyle(dropdownRef.current).width,
      );
      setStyle({
        width: `${Math.max(inputWidth, dropdownWidth)}px`,
        textAlign: 'left',
      });
    }, 0);
  };

  const isLatest = (version: string) => version === versionsHistory.latest;

  const doReference = (version: string) => {
    // The input could be 'v1' or '1.0.0'
    // If it's 'v1', find the latest version that starts with '1'
    // If it's '1.0.0', use the input version directly
    const foundVersion = versionsHistory.versions.find(
      (ver) => ver.split('.')[0] === version.slice(1),
    );
    return pathname.replace(pathname.split('/')[2], foundVersion || version);
  };

  const toggleIsOpen = (majorVersion: string) => {
    setGroupedVersions((prev) => ({
      ...prev,
      [majorVersion]: {
        ...prev[majorVersion],
        isOpen: !prev[majorVersion].isOpen,
      },
    }));
  };

  const currentVersion = (
    <div className="docspace-version">
      <span>{docSpace?.semver}</span>
      {isLatest(docSpace?.semver) && ' (latest)'}
    </div>
  );

  return (
    <>
      You are viewing documentation for version:
      {versionsHistory.majorVersions.length <= 1 && (
        <button
          ref={inputRef}
          style={style}
          className={'version-dropdown' + (show ? ' box-shadow' : '')}
          onClick={() => {
            setShow(!show);
            setShowMajor(false);
          }}
        >
          <span data-test-id="current-version">{currentVersion}</span>
          <div
            id="caret"
            className={show ? 'down-caret' : 'right-caret'}
            data-test-id="toggle-major-versions"
          ></div>
          {show && (
            <div
              ref={dropdownRef}
              style={style}
              className={`version-dropdown-items${show ? ' box-shadow' : ''}`}
            >
              {list.map((version, index) => (
                <a
                  key={index}
                  href={doReference(version)}
                  data-test-id={`specific-version-${index}`}
                >
                  {version}
                  {isLatest(version) && ' (latest)'}
                </a>
              ))}
            </div>
          )}
        </button>
      )}
      {versionsHistory.majorVersions.length > 1 && (
        <div
          ref={inputRef}
          style={style}
          className={`version-dropdown${show ? ' box-shadow' : ''}`}
        >
          <span data-test-id="current-version">{currentVersion}</span>
          <div
            id="caret"
            className={show ? 'down-caret' : 'right-caret'}
            tabIndex={0}
            onClick={() => setShow(!show)}
            data-test-id="toggle-major-versions"
            onKeyDown={(e) => e.key === 'Enter' && setShow(!show)}
            role="button"
          ></div>
          {show && (
            <div
              ref={dropdownRef}
              style={style}
              className={`version-dropdown-items${show ? ' box-shadow' : ''}`}
            >
              {list.map((version, index) => (
                <div key={index}>
                  <div
                    className="version-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <a
                      href={doReference(version)}
                      data-test-id={`major-version-${index}`}
                    >
                      {version}
                      {isLatest(version) && ' (latest)'}
                    </a>
                    {showMajor && (
                      <div
                        className={
                          groupedVersions[version]?.isOpen ?
                            'down-caret' :
                            'right-caret'
                        }
                        tabIndex={0}
                        onClick={() => toggleIsOpen(version)}
                        data-test-id={`toggle-major-version-${index}`}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && toggleIsOpen(version)
                        }
                        style={{ cursor: 'pointer', position: 'static' }}
                        role="button"
                      ></div>
                    )}
                  </div>
                  {showMajor && groupedVersions[version]?.isOpen && (
                    <div style={{ paddingLeft: '20px' }}>
                      {groupedVersions[version].versions.map(
                        (subVersion, subIndex) => (
                          <a
                            key={`${subIndex}-${subVersion}`}
                            href={doReference(subVersion)}
                            style={{ display: 'flex' }}
                            data-test-id={`specific-version-${subIndex}`}
                          >
                            {subVersion}
                            {isLatest(subVersion) && ' (latest)'}
                          </a>
                        ),
                      )}
                    </div>
                  )}
                </div>
              ))}
              {showMajor && (
                <div
                  className="show-all"
                  data-test-id="show-all"
                  tabIndex={0}
                  onClick={() => setShowMajor(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowMajor(false)}
                  role="button"
                >
                  Show All
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BannerHistory;
